import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useAccontStore } from '@/stores/AccountStore.js'
import {
	resourceSchema,
	RESOURCE_NAMES,
	readResources,
	convertToApiTimeDate,
	parseResource,
} from '@/api/api.js'
import { PowerUnit } from '@/models/powerUnit.js'




export const eventSchema = Object.freeze(Object.assign(
	{},
	resourceSchema,
	{
		eventType: Object.freeze({
			key: 'event_type',
			title: 'Тип події',
			allowed: Object.freeze({
				stabilizationSettings: Object.freeze({
					key: 'stabilization_settings',
					title: 'зміна параметрів стабілізації станції через веб-інтерфейс',
				}),
				stabilizationParametersChange: Object.freeze({
					key: 'stabilization_parameters_change',
					title: 'зміна параметрів стабілізації станції на об\'єкті',
				}),
				lightning: Object.freeze({
					key: 'lightning',
					title: 'блискавка',
				}),
				stationPowerInputOutOfRange: Object.freeze({
					key: 'station_power_input_out_of_range',
					title: 'вихід живлення станції за встановлені межі',
				}),
				unitConnection: Object.freeze({
					key: 'unit_connection',
					title: 'втрата/відновлення зв\'язку',
				}),
				stationBoxDoorState: Object.freeze({
					key: 'station_box_door_state',
					title: 'відкриття корпусу СКЗ',
				}),
			}),
		}),
		sourceObject: Object.freeze({
			key:  'source_object',

			allowed: Object.freeze({
				station: Object.freeze({
					key: 'station',
				}),
				post: Object.freeze({
					key: 'post',
				}),
			}),
		}),
		stationId: Object.freeze({
			key:  'station_id',
		}),
		postId: Object.freeze({
			key: 'post_id',
		}),
		payload: Object.freeze({
			key:  'payload',
			allowed: Object.freeze({
				// For eventType == stabilization_settings
				userId: Object.freeze({
					key: 'user_id',
				}),
				parameter: Object.freeze({
					key: 'parameter',
				}),
				value: Object.freeze({
					key: 'value',
				}),

				// For eventType == stabilization_parameters_change
				stationMode: Object.freeze({
					key: [PowerUnit.metricKeys.stationStabilizationMode],
				}),
				stationStabilizationCurent: Object.freeze({
					key: [PowerUnit.metricKeys.stationStabilizationCurent],
				}),
				stationStabilizationVoltage: Object.freeze({
					key: [PowerUnit.metricKeys.stationStabilizationVoltage],
				}),
				stationStabilizationPotential: Object.freeze({
					key: [PowerUnit.metricKeys.stationStabilizationPotential],
				}),

				// For eventType == lightning
				flashId: Object.freeze({
					key: 'flash_id',
				}),
				flashTimeUTC: Object.freeze({
					key: 'flash_time_UTC',
					title: 'час',
				}),
				distanceKm: Object.freeze({
					key: 'distance_km',
					title: 'відстань, км',
				}),
				footprintRadiusKm: Object.freeze({
					key: 'footprint_radius_km',
					title: 'радіус, км',
				}),

				// For eventType == station_power_input_out_of_range
				// value defined for eventType == stabilization_settings
				minTrashold: Object.freeze({
					key: 'min_trashold',
					title: 'Uмін',
				}),
				maxTrashold: Object.freeze({
					key: 'max_trashold',
					title: 'Uмакс',
				}),

				// For eventType == unit_connection
				unitsСonnectionState: Object.freeze({
					key: 'units_connection_state',

					powerUnit: Object.freeze({
						key: 'power_unit',
						
						old: Object.freeze({
							key: 'old',
						}),
						new: Object.freeze({
							key: 'new',
						}),
					}),
					serviceUnit: Object.freeze({
						key: 'service_unit',

						old: Object.freeze({
							key: 'old',
						}),
						new: Object.freeze({
							key: 'new',
						}),
					}),
					modemUnit: Object.freeze({
						key: 'modem_unit',

						old: Object.freeze({
							key: 'old',
						}),
						new: Object.freeze({
							key: 'new',
						}),
					}),
					breaker: Object.freeze({
						key: 'breaker',

						old: Object.freeze({
							key: 'old',
						}),
						new: Object.freeze({
							key: 'new',
						}),
					}),
					electricMeterUnit: Object.freeze({
						key: 'electric_meter_unit',

						old: Object.freeze({
							key: 'old',
						}),
						new: Object.freeze({
							key: 'new',
						}),
					}),
				}),

				// For eventType == station_box_door_state
				boxBoorState: Object.freeze({
					key: 'box_door_state',

					old: Object.freeze({
						key: 'old',
					}),
					new: Object.freeze({
						key: 'new',
					}),
				}),
			}),
		}),
	},
))

export const eventTitles = Object.freeze({
	createStationWebIf: 'Створення станції через веб-інтерфейс',
	updateStationWebIf: 'Зміна налаштувань через веб-інтерфейс',
	updateStationStabilizationParmsWebIf: eventSchema.eventType.allowed.stabilizationSettings.title,
	updateStationStabilizationParmsLockal: eventSchema.eventType.allowed.stabilizationParametersChange.title,
	createReport: 'Створено звіт',
	lightning: eventSchema.eventType.allowed.lightning.title,
	alert: 'Сповіщення',
	stationPowerInputOutOfRange: eventSchema.eventType.allowed.stationPowerInputOutOfRange.title,
	unitConnection: eventSchema.eventType.allowed.unitConnection.title,
	failure: 'Аварія/відновлення',
	stationBoxDoorState: eventSchema.eventType.allowed.stationBoxDoorState.title,
})

// "Date" type pasport fields (flatten object keys)
const EVENT_DATE_PATHS = [
	eventSchema.updateTime.key,
	eventSchema.createTime.key,
	`${eventSchema.payload.key}.${eventSchema.payload.allowed.flashTimeUTC.key}`,
]

/**
 * @param {Object} lightning - event payload
 * @returns {boolean} - true if station under lightning footprint
 */
export function isLightningAboveStaion(lightning) {
	return lightning[eventSchema.payload.allowed.distanceKm.key]
		<= lightning[eventSchema.payload.allowed.footprintRadiusKm.key]
}

/**
 * @param {Object} lightning - event payload
 * @returns {Number} - distance to lightning center if station unedr lightning footprint
 * 						else distance to lightning footprint edge
 */
export function flashDistance(lightning) {
	return isLightningAboveStaion(lightning)
		? lightning[eventSchema.payload.allowed.distanceKm.key]
		: lightning[eventSchema.payload.allowed.distanceKm.key]
			- lightning[eventSchema.payload.allowed.footprintRadiusKm.key]
}




export function useEvents() {
	const accountStore = useAccontStore()
	// Reactive lower bound for events fetch time window.
	const fromTime = ref(null)
	// Reactive upper bound for events fetch time window.
	const toTime = ref(null)
	// Event type filter - array of approved event types or null for approve all
	const eventTypes = ref(null)
	// Station id filter - array of approved station ids
	const stationIds = ref(null)

	const eventsQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: computed(() => [
			RESOURCE_NAMES.events,
			convertToApiTimeDate(fromTime.value),
			convertToApiTimeDate(toTime.value),
			...(stationIds.value ?? []),
		]),
		queryFn: async () => {
			// console.log(`fetch events from ${fromTime.value.toISOString()} to ${toTime.value.toISOString()}`)

			const isTimeRange = fromTime.value !== null || toTime.value !== null
			// Lightning event filtering by time with flashTimeUTC field instead of all other events
			let isLightningEvent = false
			let whereObject = {}
			let args = []

			if (isTimeRange) {
				whereObject[eventSchema.createTime.key] = {
					...(fromTime.value !== null && { "$gt": convertToApiTimeDate(fromTime.value) }),
					...(toTime.value !== null && { "$lt": convertToApiTimeDate(toTime.value) }),
				}
			}

			if (stationIds.value !== null) {
				whereObject[eventSchema.stationId.key] = {
					"$in": stationIds.value,
				}
			}

			// If all event types
			let filteredTypes
			if (eventTypes.value == null) {
				isLightningEvent = true
				filteredTypes = eventSchema.eventType.allowed
					.map((el) => el.key)
					.filter((type) => type !== eventSchema.eventType.allowed.lightning.key)
			// If filter to event types
			} else {
				isLightningEvent = eventTypes.value.includes(eventSchema.eventType.allowed.lightning.key)
				filteredTypes = eventTypes.value
					.filter((type) => type !== eventSchema.eventType.allowed.lightning.key)
			}
			whereObject[eventSchema.eventType.key] = {
				"$in": filteredTypes,
			}
			
			args = [ `where=${JSON.stringify(whereObject)}` ]
			
			// Read all events exept lightning
			const res = await readResources(
				RESOURCE_NAMES.events,
				accountStore.userLogin,
				accountStore.userPassword,
				args,
			)

			if (isLightningEvent) {
				whereObject = {}

				// If filter by time
				if (isTimeRange) {
					whereObject[`${eventSchema.payload.key}.${eventSchema.payload.allowed.flashTimeUTC.key}`] = {
						...(fromTime.value !== null && { "$gt": convertToApiTimeDate(fromTime.value) }),
						...(toTime.value !== null && { "$lt": convertToApiTimeDate(toTime.value) }),
					}
				}

				if (stationIds.value !== null) {
					whereObject[eventSchema.stationId.key] = {
						"$in": stationIds.value,
					}
				}

				// Filter only lightnings
				whereObject[eventSchema.eventType.key] = {
					"$eq": eventSchema.eventType.allowed.lightning.key,
				}

				args = [ `where=${JSON.stringify(whereObject)}` ]
				
				// Read lightning events
				res.push(...await readResources(
					RESOURCE_NAMES.events,
					accountStore.userLogin,
					accountStore.userPassword,
					args,
				))
			}

			return res.map((el) => parseResource(el, EVENT_DATE_PATHS))
		},
		// Keep results warm to reduce repeated network requests.
		staleTime: 10 * 60 * 1000,
	})

	const allEvents = computed(() => eventsQuery.data.value ?? [])

	// Set period or event type filters
	function setFilters(
		filters={
			fromTime: null,
			toTime: null,
			eventTypes: null,
			stationIds: null,
		}
	) {
		// console.log('setFilters', filters)
		if (filters.fromTime !== null && filters.fromTime instanceof Date) {
			fromTime.value = new Date(filters.fromTime)
		}

		if (filters.toTime !== null && filters.toTime instanceof Date) {
			toTime.value = new Date(filters.toTime)
		}

		if (filters.eventTypes !== null && Array.isArray(filters.eventTypes)) {
			eventTypes.value = [...filters.eventTypes]
		}

		if (filters.stationIds !== null && Array.isArray(filters.stationIds)) {
			stationIds.value = [...filters.stationIds]
		}
	}

	return {
		eventSchema,

		allEvents,
		isLoading: eventsQuery.isLoading,
		isError: eventsQuery.isError,
		error: eventsQuery.error,
		
		setFilters,
	}
}